import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MasterDataSeriveService } from '../../../../Services/master-data-serive.service';
import { CustomToastsService } from '../../../../Services/custom-toasts.service';
import {
  LicensePlates,
  OverrideWithLicensePlates,
  ProductionOverride,
  ProductionOverrideLicensePlates,
  Stores,
  WarehouseLocationsModels,
  WarehouseModel,
} from '../../../../Models/ApplicationModels';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  finalize,
  firstValueFrom,
  from,
  of,
  startWith,
  Subject,
  takeUntil
} from 'rxjs';
import {
  LicensePlateAssociationName,
  LicensePlateStatus
} from 'src/app/Services/enum';
import {
  OverrideStatus,
  OverrideType,
  Status,
  PrinterType,
} from 'src/app/enum/enum';
import { CommonServiceService } from 'src/app/Services/common-service.service';
import { AdminBreadcrumbsService } from '../../admin-services/admin-breadcrumbs.service';
import { environment } from 'src/app/environments/environment';
import { QzTrayService } from 'src/app/qz-tray.service';
import { WeekColorService } from '../../company-config/WeekColors/services/week-color.service';
import { ProductionCycleSchedule,
  ColorSchedule } from '../../company-config/WeekColors/models/week-color.model';

@Component({
  selector: 'app-override',
  templateUrl: './override.component.html',
  styleUrls: ['./override.component.css']
})
export class OverrideComponent implements OnInit, OnDestroy {
  @ViewChild('labelFormatModalCloseBtn') labelFormatModalCloseBtn!: ElementRef;

  overrideForm!: FormGroup;
  licensePlateForm!: FormGroup;

  stores$ = new BehaviorSubject<Stores[]>([]);
  filteredLocations$ = new BehaviorSubject<WarehouseLocationsModels[]>([]);
  overridesList$ = new BehaviorSubject<OverrideWithLicensePlates[]>([]);

  private destroy$ = new Subject<void>();
  private readonly userDetails = this.masterService.getUserDetailsFromStorage();
  private allOverrides: OverrideWithLicensePlates[] = [];
  companyId = this.userDetails.companyID;
  currentStore = this.userDetails.storeId;
  defaultLocationId = this.userDetails.defaultProcessorLocationID;
  selectedTab: 'current' | 'all' | 'expired' = 'current';
  isLoadingOverrides: boolean = false;
  searchTerm: string = '';
  selectedOverride: OverrideWithLicensePlates | null = null;
  licensePlates: LicensePlates[] = [];
  locations: WarehouseLocationsModels[] = [];
  storeMap = new Map<number, string>();

  selectedBgColor = '';
  isAddingNewLP = false;
  selectedPlateId = '0';
  showDisablePopup = false;
  showWarning = false;
  associateLicensePlate: ProductionOverrideLicensePlates =
    new ProductionOverrideLicensePlates();
  licenseplate: LicensePlates = new LicensePlates();
  emptyLp: LicensePlates | null = null;
  associateEmptyLp: boolean = false;
  emptyLicensePlates: Array<LicensePlates> = [];
  minReminderDate!: string;
  OverrideTypes = OverrideType;
  weekColorSchedule: Array<ProductionCycleSchedule> = [];
  selectedWeekColorObj: ProductionCycleSchedule | null = null;
  warehouses: WarehouseModel[] = [];
  showWeekColor: boolean = true;
  showWeeklyOverrideOption: boolean = true;

  // Print functionality properties
  licensePlatesToPrint: LicensePlates[] = [];
  selectedLabelFormat: any | null = null;
  labelFormats: any[] = [];
  isPrinting: boolean = false;
  printingOverrideId: number | null = null;

  // Copy functionality properties
  copyingId: number | null = null;

  get isEditMode(): boolean {
    return (this.selectedOverride?.id ?? 0) > 0;
  }

  get isFormValid(): boolean {
    return this.overrideForm?.valid && this.isWeekDateValid();
  }

  constructor(
    private fb: FormBuilder,
    private masterService: MasterDataSeriveService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private toastr: CustomToastsService,
    private errorService: CommonServiceService,
    private qzService: QzTrayService,
    private weekColorService: WeekColorService,
  ) {}

  ngOnInit(): void {
    const productionScheduleType = (this.userDetails?.productionScheduleType ?? '')
      .toString()
      .trim()
      .toLowerCase();
    this.showWeekColor = productionScheduleType !== 'datespecific';
    this.showWeeklyOverrideOption = this.showWeekColor;

    this.qzService.initQZ();
    this.minReminderDate = this.getTodayDate();
    this.initializeForms();
    this.setupBreadcrumbs();
    this.initializeData().then(() => {
      this.applyFilters();
    });
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getTodayDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  private initializeForms(): void {
    this.overrideForm = this.fb.group({
      description: ['', [Validators.required, Validators.maxLength(50)]],
      destinationStoreId: [0, [Validators.required, Validators.min(1)]],
      sourceStoreId: [0],
      warehouseLocationId: [0],
      weekColorDate: ['', Validators.required],
      setReminder: [false],
      reminderDate: [''],
      weekColor: [''],
      type: [OverrideType.DateSpecific, Validators.required]
    });

    this.licensePlateForm = this.fb.group({
      description: ['', Validators.required],
      warehouseLocationId: [0, [Validators.required, Validators.min(1)]]
    });
  }

  private setupBreadcrumbs(): void {
    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Inventory Management', link: 'demo' },
      { crumbName: 'Overrides', link: '/admin/home/overrides' }
    ]);
  }

  private async initializeData(): Promise<void> {
    try {
      await Promise.all([
        this.loadStores(),
        this.loadLocations(),
        this.loadOverrides()
      ]);
      this.getAllLabelFormats();
    } catch (error) {
      this.toastr.error('Failed to load initial data');
      console.error('Initialization error:', error);
    }
  }

  futureDateValidator(control: AbstractControl) {
    if (!control.value) return null;

    const selected = new Date(control.value);
    selected.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selected >= today ? null : { notFutureDate: true };
  }

  private setupFormSubscriptions(): void {
    // Watch source store changes to load locations for LP and enforce location validation
    this.overrideForm
      .get('sourceStoreId')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        startWith(0)
      )
      .subscribe((storeId) => this.onSourceStoreChange(storeId));

    // Watch week color date changes
    this.overrideForm
      .get('weekColorDate')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(300)
      )
      .subscribe((date) => {
        if (date) this.handleWeekColorDateChange(date);
      });

    this.overrideForm
      .get('setReminder')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        const reminderCtrl = this.overrideForm.get('reminderDate');

        if (enabled) {
          reminderCtrl?.setValidators([
            Validators.required,
            this.futureDateValidator
          ]);
        } else {
          reminderCtrl?.clearValidators();
          reminderCtrl?.setValue(null);
        }

        reminderCtrl?.updateValueAndValidity();
      });
  }

  private loadStores(): Promise<void> {
    return new Promise((resolve, reject) => {
      (
        this.masterService.getallstores(
          this.userDetails.companyID
        ) as import('rxjs').Observable<Stores[]>
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (stores: Stores[]) => {
            const activeStores = stores;
            this.stores$.next(activeStores);
            this.buildStoreMap(activeStores);
            resolve();
          },
          error: (error) => {
            console.error('Error loading stores:', error);
            reject(error);
          }
        });
    });
  }

  private loadOverrides(): Promise<void> {
    // Show the loader and clear the existing rows so stale data isn't shown while fetching.
    this.isLoadingOverrides = true;
    this.allOverrides = [];
    this.overridesList$.next([]);

    return new Promise((resolve, reject) => {
      (
        this.masterService.getProductionOverrides(
          this.userDetails.companyID,
          { scope: this.tabToScope(this.selectedTab) }
        ) as import('rxjs').Observable<OverrideWithLicensePlates[]>
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (overrides: OverrideWithLicensePlates[]) => {
            // Ensure licensePlates array exists
            const processedOverrides = overrides.map((override) => ({
              ...override,
              licensePlates: override.licensePlates || []
            }));
            // The API has already scoped by date for the active tab; only the
            // search term is applied client-side from here on.
            this.allOverrides = processedOverrides;
            this.applyFilters();
            this.isLoadingOverrides = false;
            resolve();
          },
          error: (error) => {
            this.isLoadingOverrides = false;
            this.toastr.error('Failed to load overrides');
            reject(error);
          }
        });
    });
  }

  private tabToScope(
    tab: 'current' | 'all' | 'expired'
  ): 'Current' | 'Expired' | 'All' {
    if (tab === 'expired') {
      return 'Expired';
    }
    if (tab === 'all') {
      return 'All';
    }
    return 'Current';
  }

  private loadLocations(): Promise<void> {
    return new Promise((resolve, reject) => {
      (
        this.masterService.getWarehouseLocations(
          this.userDetails.companyID,
          this.userDetails.storeId
        ) as import('rxjs').Observable<WarehouseLocationsModels[]>
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (locations: WarehouseLocationsModels[]) => {
            this.locations = locations;
            this.updateFilteredLocations();
            resolve();
          },
          error: (error) => {
            console.error('Error loading locations:', error);
            reject(error);
          }
        });
    });
  }

  private loadLocationsForStore(storeId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      (
        this.masterService.getWarehouseLocations(
          this.userDetails.companyID,
          storeId
        ) as import('rxjs').Observable<WarehouseLocationsModels[]>
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (locations: WarehouseLocationsModels[]) => {
            this.locations = locations;
            this.updateFilteredLocations();
            resolve();
          },
          error: (error) => {
            console.error('Error loading locations for store:', error);
            reject(error);
          }
        });
    });
  }
  loadWarehouses(storeId: number): void {
    this.masterService
      .getWarehousesLite(this.userDetails.companyID, storeId)
      .subscribe({
        next: (res) => {
          this.warehouses = res as WarehouseModel[];
          this.updateFilteredLocations();
        },
        error: () => {
          this.toastr.error('Failed to load warehouses');
        }
      });
  }
  getWarehouseName(warehouseId: number): string {
    const warehouse = this.warehouses.find((w) => w.id === warehouseId);
    return warehouse ? warehouse.name : '';
  }

  onSourceStoreChange(storeId: number): void {
    const locCtrl = this.overrideForm.get('warehouseLocationId');

    if (storeId && storeId > 0) {
      // Load locations for selected source store
      this.loadLocationsForStore(storeId)
        .then(() => {
          // Only require location when creating a new override. In edit mode keep it optional
          if (!this.isEditMode) {
            locCtrl?.setValidators([Validators.required, Validators.min(1)]);
          } else {
            locCtrl?.clearValidators();
          }
          locCtrl?.updateValueAndValidity();
          this.loadWarehouses(storeId);
        })
        .catch(() => {
          locCtrl?.clearValidators();
          locCtrl?.updateValueAndValidity();
        });
    } else {
      // Clear locations and validation
      this.locations = [];
      locCtrl?.clearValidators();
      locCtrl?.setValue(0);
      locCtrl?.updateValueAndValidity();
    }
  }

  private buildStoreMap(stores: Stores[]): void {
    this.storeMap.clear();
    stores.forEach((store) =>
      this.storeMap.set(store.storeId, store.storeName)
    );
  }

  private updateFilteredLocations(): void {
    if (!this.locations) {
      this.filteredLocations$.next([]);
      return;
    }

    if (this.warehouses && this.warehouses.length > 0) {
      const warehouseIds = new Set(
        this.warehouses.map((w) => Number((w as any).id))
      );
      const locationWarehouseIds = Array.from(
        new Set(this.locations.map((l: any) => Number((l as any).warehouseId)))
      );

      const filtered = this.locations.filter((loc: any) =>
        warehouseIds.has(Number(loc.warehouseId))
      );

      this.filteredLocations$.next(filtered);
    } else {
      this.filteredLocations$.next(this.locations);
    }
  }
  private handleWeekColorDateChange(date: string): void {
    if (!this.showWeekColor) {
      this.overrideForm.patchValue(
        {
          weekColorDate: date
        },
        { emitEvent: false }
      );
      return;
    }
    this.weekColorService
      .getProductionCycleScheduleByDate(this.companyId, date)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (weekColor) => {
          const wc = weekColor as ColorSchedule;
          this.selectedBgColor = wc.color;
          this.overrideForm.patchValue(
            {
              weekColor: wc.displayName,
              weekColorDate: date
            },
            { emitEvent: false }
          );
        },
        () => this.toastr.error('Failed to load week color')
      );
  }

  private isWeekDateValid(): boolean {
    const weekColorDate = this.overrideForm.get('weekColorDate')?.value;
    if (!weekColorDate) return false;

    const today = new Date().toISOString().split('T')[0];
    return weekColorDate >= today;
  }

  getStoreName(storeId: number): string {
    return this.storeMap.get(storeId) ?? 'Unknown Store';
  }

  openDatePicker(dateInput: HTMLInputElement): void {
    dateInput?.showPicker?.();
  }

  onLicensePlateSelectionChange(): void {
    if (this.selectedPlateId === 'new') {
      this.isAddingNewLP = true;
      this.licensePlateForm.reset();
    } else if (this.selectedPlateId === 'empty') {
      this.associateEmptyLp = true;
      this.licensePlateForm.reset();
      this.getEmptyLicensePlate();
    } else {
      this.isAddingNewLP = false;
      this.associateEmptyLp = false;
    }
  }

  async editOverride(override: OverrideWithLicensePlates): Promise<void> {
    this.selectedOverride = { ...override };
    this.licensePlates =
      override.licensePlates?.map((lp) => lp.licensePlate) || [];

    // Populate form
    this.overrideForm.patchValue({
      description: override.description,
      destinationStoreId: override.destinationStoreId,
      sourceStoreId: override.sourceStoreId,
      warehouseLocationId: override.warehouseLocationId,
      weekColorDate: override.weekColorDate,
      setReminder: !!override.reminderDate,
      reminderDate: override.reminderDate,
      weekColor: override.weekColor,
      type: override.type
    });

    if (this.showWeekColor && override.type === OverrideType.WeeklySchedule) {
      await this.getWeekColorsSchedule();

      this.selectedWeekColorObj =
        this.weekColorSchedule.find(
          (w) => w.displayName === override.weekColor,
        ) || null;
    }

    this.selectedBgColor = override.weekColor;
    this.isAddingNewLP = false;
    this.selectedPlateId = '0';
  }

  async addNewLicensePlate(): Promise<void> {
    if (!this.licensePlateForm.valid) {
      this.toastr.error('Please fill all license plate fields');
      return;
    }

    const newLP: LicensePlates = {
      ...this.licensePlateForm.value,
      associationId: this.selectedOverride?.id || 0,
      associationName: LicensePlateAssociationName.Store,
      warehouseLocationId: this.licensePlateForm.get('warehouseLocationId')
        ?.value,
      companyId: this.companyId,
      status: LicensePlateStatus.Available,
      referenceNumber: this.currentStore.toString()
    };

    try {
      const result = await firstValueFrom(
        this.masterService.addLicensePlate(newLP)
      );

      if (result) {
        this.toastr.success('License plate has been added successfully...');
        this.licenseplate = result as LicensePlates;
        this.isAddingNewLP = false;
        this.selectedPlateId = '0';
        this.licensePlateForm.reset();
        this.getAllLicensePlates();
      }
    } catch (error) {
      this.toastr.error('Failed to add license plate');
      console.error('Error adding license plate:', error);
    }
  }

  public async updateLicensePlateStatusIfEmpty(
    licenseplate: LicensePlates | null
  ): Promise<void> {
    if (!licenseplate) {
      console.warn('No license plate selected');
      return;
    }

    licenseplate.status = LicensePlateStatus.Available;
    licenseplate.associationId = this.selectedOverride?.id || 0;
    licenseplate.associationName = LicensePlateAssociationName.Store;

    this.masterService
      .UpdateLicensePlate(licenseplate.id, licenseplate)
      .subscribe({
        next: () => {
          this.associateEmptyLp = false;
          this.getAllLicensePlates();
        },
        error: (err) => {
          console.error('Error updating license plate', err);
        }
      });
  }

  associateLicensePlates() {
    this.associateLicensePlate.licensePlateId = this.licenseplate.id;
    this.associateLicensePlate.productionOverrideId =
      this.selectedOverride?.id || 0;
    this.selectedOverride?.id || 0;
    this.masterService
      .associateLicensePlate(
        this.associateLicensePlate.productionOverrideId,
        this.associateLicensePlate
      )
      .subscribe((res) => {
        if (res) {
        }
      });
  }

  getAllLicensePlates() {
    this.masterService
      .getAllLicensePlates(this.companyId, this.selectedOverride?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.licensePlates = res as LicensePlates[];
          this.selectedOverride!.licensePlates = this.licensePlates.map(
            (lp) => ({
              licensePlateId: lp.id,
              productionOverrideId: this.selectedOverride!.id,
              licensePlate: lp
            })
          );
        }
      });
  }
  async saveOverride(): Promise<void> {
    if (!this.isFormValid) {
      this.toastr.error('Please fill all required fields correctly');
      return;
    }

    const raw = this.overrideForm.value;

    // Build payload manually (boundary normalization)
    const override: ProductionOverride = {
      ...(raw as any),
      reminderDate:
        raw.reminderDate && raw.reminderDate.trim() ? raw.reminderDate : null,
      weekColor: raw.weekColor ?? '',
      id: this.selectedOverride?.id || 0,
      companyId: this.userDetails.companyID,
      status: OverrideStatus.Active,
      type: raw.type ?? OverrideType.DateSpecific
    };

    // Remove UI-only field
    delete (override as any).setReminder;

    try {
      if (this.isEditMode) {
        await this.updateOverride(override);
      } else {
        await this.createOverride(override);
      }

      this.resetForm();
      this.loadOverrides();
    } catch (error: any) {
      const action = this.isEditMode ? 'update' : 'create';
      const errorMessage = this.errorService.extractErrorMessage(
        error,
        `Failed to ${action} override`
      );
      this.toastr.error(errorMessage);
    }
  }

  private async createOverride(override: ProductionOverride): Promise<void> {
    try {
      override.sourceStoreId =
        this.overrideForm.get('sourceStoreId')?.value > 0
          ? this.overrideForm.get('sourceStoreId')?.value
          : this.currentStore;

      const createdOverride = (await firstValueFrom(
        this.masterService.addProductionOverride(override)
      )) as ProductionOverride;

      if (!createdOverride) {
        throw new Error('Override creation returned null');
      }

      const selectedLocationId = this.overrideForm.get(
        'warehouseLocationId'
      )?.value;
      const defaultLocation =
        selectedLocationId > 0 ? selectedLocationId : this.defaultLocationId;

      const newLP: LicensePlates = {
        id: 0,
        associationId: createdOverride.id,
        associationName: LicensePlateAssociationName.Store,
        warehouseLocationId: defaultLocation ?? 0,
        description: override.description,
        companyId: this.companyId,
        status: LicensePlateStatus.Available,
        referenceNumber: this.currentStore.toString(),
        createdAt: '',
        createdById: 0,
        lastModifiedAt: '',
        lastModifiedById: 0
      };

      const lpResult = await firstValueFrom(
        this.masterService.addLicensePlate(newLP)
      );

      if (!lpResult) {
        this.toastr.error('Override created but license plate creation failed');
        return;
      }

      const createdLP = lpResult as LicensePlates;

      this.toastr.success(
        'Override created successfully with default license plate'
      );
      this.licenseplate = createdLP;
      this.selectedOverride = {
        ...createdOverride,
        isDeletable: true,
        warehouseLocationId: createdLP.warehouseLocationId ?? 0,
        licensePlates: [
          {
            productionOverrideId: createdOverride.id,
            licensePlateId: createdLP.id,
            licensePlate: createdLP
          }
        ]
      };
    } catch (error: any) {
      const errorMessage = this.errorService.extractErrorMessage(
        error,
        'Something went wrong while creating override'
      );
      this.toastr.error(errorMessage);
    }
  }

  private async updateOverride(override: ProductionOverride): Promise<void> {
    try {
      const result = await this.masterService
        .updateProductionOverride(override.id, override)
        .toPromise();

      if (result) {
        this.toastr.success('Override updated successfully');
      } else {
        throw new Error('Update returned no result');
      }
    } catch (error: any) {
      const errorMessage = this.errorService.extractErrorMessage(
        error,
        'Failed to update override'
      );
      this.toastr.error(errorMessage);
    }
  }

  resetForm(): void {
    this.overrideForm.reset({
      destinationStoreId: 0,
      sourceStoreId: 0,
      warehouseLocationId: 0,
      description: '',
      weekColorDate: '',
      reminderDate: '',
      weekColor: '',
      type: OverrideType.DateSpecific
    });

    this.licensePlateForm.reset({
      warehouseLocationId: 0,
      description: ''
    });

    this.selectedOverride = null;
    this.selectedPlateId = '0';
    this.isAddingNewLP = false;
    this.selectedBgColor = '';
    this.licensePlates = [];
    this.selectedWeekColorObj = null;
  }

  trackByOverrideId(index: number, item: OverrideWithLicensePlates): number {
    return item.id;
  }

  trackByStoreId(index: number, item: Stores): number {
    return item.storeId;
  }

  trackByLocationId(index: number, item: WarehouseLocationsModels): number {
    return item.id;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.searchOverrides(value);
  }

  applyFilters() {
    // Date scoping is handled server-side per tab; only the search term is applied here.
    let data = this.allOverrides;
    const trimmedTerm = (this.searchTerm || '').trim().toLowerCase();
    if (trimmedTerm) {
      data = data.filter((o) =>
        o.description?.toLowerCase().includes(trimmedTerm)
      );
    }
    this.overridesList$.next(data);
  }

  changeTab(tab: 'current' | 'all' | 'expired') {
    if (this.selectedTab === tab) {
      return;
    }
    this.selectedTab = tab;
    // Re-fetch from the API with the new date scope.
    this.loadOverrides();
  }
  searchOverrides(term: string): void {
    this.searchTerm = term;
    this.applyFilters();
  }
  openDisablePopup(override: OverrideWithLicensePlates) {
    this.showDisablePopup = true;
    this.selectedOverride = override;
    this.showWarning = false;
  }

  closePopup() {
    this.showDisablePopup = false;
  }

  disableFeature() {
    if (!this.selectedOverride?.isDeletable) {
      this.showWarning = true;
      return;
    }
    this.proceedWithDisable();
  }

  proceedWithDisable() {
    this.masterService
      .deleteProductionOverride(this.selectedOverride?.id || 0)
      .subscribe(
        async () => {
          this.toastr.success('Override has been disabled successfully...');
          this.resetForm();
          await this.loadOverrides();
          this.changeTab('current');
          this.closePopup();
        },
        (err) => {
          const msg = this.errorService.extractErrorMessage(
            err,
            'Failed to disable override'
          );
          this.toastr.error(msg);
        }
      );
  }
  async getEmptyLicensePlate() {
    this.emptyLicensePlates = await firstValueFrom(
      this.masterService.getEmptyLicensePlate(this.companyId)
    );
    this.emptyLicensePlates = this.emptyLicensePlates.filter(
      (lp) =>
        lp.status === LicensePlateStatus.Available &&
        this.locations.some((loc) => loc.id === lp.warehouseLocationId)
    );
  }

  onOverrideTypeChange(): void {
    const type = this.overrideForm.get('type')?.value;

    this.overrideForm.patchValue(
      {
        weekColor: '',
        weekColorDate: ''
      },
      { emitEvent: false }
    );
    this.selectedBgColor = '';
    this.selectedWeekColorObj = null;

    if (type === OverrideType.WeeklySchedule) {
      if (this.weekColorSchedule.length === 0) {
        this.getWeekColorsSchedule();
      }
    }
  }

  onWeekColorSelected(selectedColor: ProductionCycleSchedule | null): void {
    if (
      selectedColor &&
      selectedColor.startDate &&
      selectedColor.displayName
    ) {
      this.overrideForm.patchValue(
        {
          weekColor: selectedColor.displayName,
          weekColorDate: new Date(selectedColor.endDate)
            .toISOString()
            .split('T')[0]
        },
        { emitEvent: false }
      );
      this.selectedBgColor = selectedColor.color;
    }
  }

  async getWeekColorsSchedule(): Promise<void> {
    if (!this.showWeekColor) return;
    try {
      const res = await this.weekColorService.getProductionCycleSchedule(this.companyId).toPromise();

      this.weekColorSchedule = res as ProductionCycleSchedule[];
    } catch (error) {
      console.error('Failed to load week color schedule', error);
    }
  }

  openLicensePlateLink(licensePlateId: number | string): void {
    const id = encodeURIComponent(String(licensePlateId));

    const url =
      `${environment.inventoryWebBaseUrl}` +
      `?redirect=/inventory/license-plate&id=${id}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  copyOverride(overrideId: number): void {
    this.copyingId = overrideId;
    this.masterService.copyProductionOverride(overrideId).subscribe({
      next: async () => {
        this.toastr.success('Override copied successfully');
        await this.loadOverrides();
        this.changeTab('current');
      },
      error: (error) => {
        const errorMessage = this.errorService.extractErrorMessage(
          error,
          'Failed to copy override'
        );
        this.toastr.error(errorMessage);
        this.copyingId = null;
      },
      complete: () => {
        this.copyingId = null;
      }
    });
  }

  canCopyOverride(override: OverrideWithLicensePlates): boolean {
    function dateOnly(date: Date): Date {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    const todayOnly = dateOnly(new Date());
    const overrideDate = new Date(override.weekColorDate);
    return overrideDate < todayOnly;
  }

  // Print functionality methods
  onPrintClick(override: any, event?: Event): void {
    if (this.isPrinting) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }
    if (override.licensePlates && override.licensePlates.length > 0) {
      this.printingOverrideId = override.id ?? null;
      this.readyToPrint(override);
      if (this.labelFormats.length === 1) {
        event?.preventDefault();
        event?.stopPropagation();
        this.selectedLabelFormat = this.labelFormats[0];
        this.confirmPrint();
      } else if (this.labelFormats.length === 0) {
        this.getAllLabelFormats();
      }
    }
  }

  readyToPrint(override: any): void {
    // Extract actual license plates from the nested structure
    this.licensePlatesToPrint =
      override.licensePlates?.map((lp: any) => lp.licensePlate) || [];
    this.selectedLabelFormat = null;
  }

  getAllLabelFormats(): void {
    this.masterService
      .getAllInventoryLabels(this.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.labelFormats = Array.isArray(res) ? res : [];
        },
        error: (err: any) => {
          this.toastr.error('Failed to load label formats');
          console.error('Error loading label formats:', err);
        }
      });
  }

  confirmPrint(): void {
    if (!this.selectedLabelFormat || !this.selectedLabelFormat.zplCode) {
      this.toastr.error('Please select a label format');
      return;
    }

    if (this.licensePlatesToPrint.length === 0) {
      this.toastr.error('No license plates to print');
      return;
    }

    this.isPrinting = true;
    this.printAllLicensePlates();
  }

  private getPrinterNameFromLabelFormat(labelFormat: any): string | null {
    const printerType = Number(
      labelFormat?.printerType ?? labelFormat?.printerTypeId ?? 0
    );

    if (printerType === 1) return 'ZebraLabel';
    if (printerType === 2) return 'ZebraTag';
    if (printerType === 3) return 'ZebraBoth';

    return null;
  }

  printAllLicensePlates(): void {
    if (!this.selectedLabelFormat?.zplCode) {
      this.toastr.error('ZPL template missing');
      this.isPrinting = false;
      this.printingOverrideId = null;
      return;
    }

    const printerName = this.getPrinterNameFromLabelFormat(
      this.selectedLabelFormat
    );
    if (!printerName) {
      this.toastr.error('Printer type not configured for selected label');
      this.isPrinting = false;
      this.printingOverrideId = null;
      return;
    }

    const zplTemplate = this.selectedLabelFormat.zplCode;
    const userDetails = this.masterService.getUserDetailsFromStorage();
    const companyName = userDetails.company || '';

    const printablePlates = this.licensePlatesToPrint.filter(
      (lp) => !!lp.referenceNumber
    );

    let successCount = 0;
    let errorCount = this.licensePlatesToPrint.length - printablePlates.length;

    from(printablePlates)
      .pipe(
        concatMap((lp) => {
          const finalZpl = zplTemplate
            .replace(/\{{1,2}CompanyName\}{1,2}/g, companyName)
            .replace(/\{{1,2}LicensePlateNo\}{1,2}/g, lp.referenceNumber || '')
            .replace(/\{{1,2}Description\}{1,2}/g, lp.description || '')
            .replace(/\{{1,2}BarcodeValue\}{1,2}/g, lp.referenceNumber || '');

          return this.qzService.printZpl(printerName, finalZpl).pipe(
            catchError((err: any) => {
              errorCount++;
              console.error(
                'Print error for license plate',
                lp.referenceNumber,
                err
              );
              return of(null);
            })
          );
        }),
        finalize(() => {
          if (successCount > 0) {
            this.toastr.success(
              `${successCount} license plates printed successfully`
            );
          }
          if (errorCount > 0) {
            this.toastr.error(`${errorCount} license plates failed to print`);
          }
          this.isPrinting = false;
          this.printingOverrideId = null;
          this.labelFormatModalCloseBtn.nativeElement.click();
        })
      )
      .subscribe(() => {
        successCount++;
      });
  }
}
